import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import type { InAppNotification } from '../types';
import { formatEventDate, stripEventDateFromBody } from '../utils/notificationDisplay';

interface NotificationItemProps {
  readonly notification: InAppNotification;
  readonly onPress: (notification: InAppNotification) => void;
  readonly isExpanded?: boolean;
  readonly onToggleExpanded?: (notificationId: string) => void;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onPress,
  isExpanded = false,
  onToggleExpanded,
}) => {
  const eventDateLabel = formatEventDate(notification.eventDate || '');

  return (
    <TouchableOpacity
      style={[styles.container, !notification.isRead && styles.unread]}
      activeOpacity={0.8}
      onPress={() => {
        if (notification.isRead && onToggleExpanded) {
          onToggleExpanded(notification.id);
          return;
        }
        onPress(notification);
      }}
    >
      <View style={styles.titleRow}>
        <Text style={styles.title}>{notification.title}</Text>
        {!notification.isRead && <View style={styles.unreadDot} />}
      </View>
      <Text style={styles.body} numberOfLines={notification.isRead && !isExpanded ? 1 : undefined}>
        {stripEventDateFromBody(notification.body, notification.eventDate)}
      </Text>
      {notification.isRead && (
        <Text style={styles.expandHint}>{isExpanded ? 'Hide details' : 'Tap to expand'}</Text>
      )}
      {eventDateLabel ? <Text style={styles.date}>{`Event date: ${eventDateLabel}`}</Text> : null}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 10,
    padding: 12,
    marginBottom: 10,
    backgroundColor: '#FFFFFF',
  },
  unread: {
    borderColor: '#BFDBFE',
    backgroundColor: '#F8FAFF',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  title: {
    fontSize: 14,
    color: '#111827',
    fontFamily: 'Montserrat_600SemiBold',
    flex: 1,
    marginRight: 8,
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#2563EB',
  },
  body: {
    fontSize: 13,
    color: '#374151',
    fontFamily: 'Montserrat_500Medium',
    marginBottom: 8,
  },
  date: {
    fontSize: 12,
    color: '#6B7280',
    fontFamily: 'Montserrat_500Medium',
  },
  expandHint: {
    fontSize: 11,
    color: '#6B7280',
    fontFamily: 'Montserrat_500Medium',
    marginBottom: 6,
  },
});
